import { HttpEvent, HttpEventType, HttpResponse } from '@angular/common/http';
import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { firstValueFrom, Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { PopupComponent } from '@libs/front/components/popup/popup.component';
import { ButtonComponent } from '@libs/front/components/button/button.component';
import { CheckboxComponent } from '@libs/front/components/checkbox/checkbox.component';
import { SelectComponent, SelectOption } from '@libs/front/components/select/select.component';
import { InputComponent } from '@libs/front/components/input/input.component';
import { ScrollableComponent } from 'src/app/shared/components/scrollable/scrollable.component';
import { HttpService } from 'src/app/services/http.service';
import { FonctionsService } from 'src/app/services/fonctions-partagees.service';

/** Champs auxquels une colonne du fichier peut être associée. */
export type CsvTargetField =
  'name' | 'x' | 'y' | 'z' | 'length' | 'diameter' | 'azimuth' | 'angle' | 'ignore';

export enum CsvHoleDataType {
  Planned = 0,
  Measured = 1
}

export enum CsvPlannedImportMode {
  Replace = 0,
  Append = 1
}

export enum CsvMeasuredMatchingMethod {
  ByName = 0,
  ByNearestNeighbour = 1
}

export interface CsvImportIssue {
  severity: number; // 0 = avertissement, 1 = erreur
  code: string;
  message: string;
  lineNumber?: number;
  holeName?: string;
}

export interface CsvValidationResult {
  detectedHoles: number;
  validHoles: number;
  issues: CsvImportIssue[];
  canImport: boolean;
  warningCount: number;
  errorCount: number;
}

/**
 * Corps renvoyé par l'import STR/DXF, et seulement lorsque le fichier porte un nom
 * de tir différent de l'actuel : le back ne répond un corps que dans ce cas
 * (BlastsController.ImportBlastFromStr), sinon la réponse est vide.
 */
export interface StrImportResult {
  blastId: number;
  blastName: string;
}

/**
 * Assistant d'import CSV des têtes de trou, en quatre étapes :
 * fichier, mappage des colonnes, méthode, puis validation avant écriture.
 *
 * Aucune règle de validation n'est réimplémentée ici : l'étape 4 interroge le
 * back, qui est seul dépositaire des règles, pour que l'aperçu corresponde
 * exactement à ce que fera l'import.
 *
 * Affiché dans le popup du kit (`app-popup`) : l'assistant repart de zéro à chaque
 * ouverture, et `closed` est émis à chaque sortie pour que le parent recharge les trous.
 */
@Component({
  selector: 'app-csv-hole-import-modal',
  templateUrl: './csv-hole-import-modal.component.html',
  styleUrls: ['./csv-hole-import-modal.component.scss'],
  standalone: true,
  imports: [
    CommonModule, FormsModule, TranslateModule,
    PopupComponent, ButtonComponent, CheckboxComponent, SelectComponent, InputComponent, ScrollableComponent
  ]
})
export class CsvHoleImportModalComponent implements OnChanges {
  @Input() isOpen = false;
  @Input() blastId: number;
  @Input() blastName = '';
  @Input() plannedHoleCount = 0;
  /** EPSG du site, repris comme EPSG source à chaque ouverture */
  @Input() siteEpsg: number | null = null;

  @Output() closed = new EventEmitter<void>();
  @Output() imported = new EventEmitter<void>();

  private blastChanged = false;

  protected readonly step = signal(1);
  protected readonly busy = signal(false);

  protected readonly fileName = signal('');
  protected readonly fileSizeKb = signal(0);
  private file: File | null = null;

  /** En-têtes du fichier, dans l'ordre, renvoyés par le back */
  protected readonly headers = signal<string[]>([]);
  /** Association en-tête → champ cible */
  protected readonly mapping = signal<Record<string, CsvTargetField>>({});

  /**
   * Pré-rempli à l'ouverture avec l'EPSG du site : c'est le cas de loin le plus
   * fréquent, et la reprojection devient alors neutre. L'utilisateur reste libre de
   * le corriger si son relevé vient d'un autre système.
   */
  sourceEpsg: number | null = null;

  protected dataType: CsvHoleDataType | null = null;
  protected plannedMode = CsvPlannedImportMode.Replace;
  protected matchingMethod = CsvMeasuredMatchingMethod.ByName;
  protected maxMatchingDistance = 1.5;

  /**
   * Inverse X et Y avant tout traitement. Réservé au STR et au DXF, qui portent leur
   * propre structure : coché par défaut car beaucoup d'exports stockent X = northing.
   *
   * Volontairement absent de l'import CSV : l'utilisateur y désigne lui-même sa colonne
   * X et sa colonne Y à l'étape de mappage, et une inversion appliquée par-dessus ne
   * ferait qu'annuler ce choix explicite.
   */
  protected swapXY = true;

  protected readonly validation = signal<CsvValidationResult | null>(null);

  /**
   * Nom de tir porté par le fichier STR, renseigné uniquement s'il diffère de l'actuel.
   * Renseigné, il déclenche l'étape 5 : l'import est déjà écrit, seul le renommage
   * reste à confirmer.
   */
  protected readonly proposedBlastName = signal<string | null>(null);

  // Exposés au template
  protected readonly DataType = CsvHoleDataType;
  protected readonly PlannedMode = CsvPlannedImportMode;
  protected readonly Matching = CsvMeasuredMatchingMethod;

  /** Champs cibles proposés pour chaque colonne, traduits à l'ouverture */
  protected fieldOptions: SelectOption[] = [];

  constructor(
    private readonly http: HttpService,
    private readonly fonctionsService: FonctionsService,
    private readonly translate: TranslateService
  ) { }

  /** Chaque ouverture repart d'un assistant vierge, l'EPSG source repris du site. */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isOpen'] && this.isOpen) this.reset();
  }

  // ------------------------------------------------------------- étape 1

  protected onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) return;
    this.setFile(input.files[0]);
  }

  protected onFileDropped(event: DragEvent): void {
    event.preventDefault();
    const dropped = event.dataTransfer?.files?.[0];
    if (dropped) this.setFile(dropped);
  }

  protected allowDrop(event: DragEvent): void {
    event.preventDefault();
  }

  private setFile(file: File): void {
    this.file = file;
    this.fileName.set(file.name);
    this.fileSizeKb.set(Math.round(file.size / 102.4) / 10);
    this.isCsv.set(file.name.toLowerCase().endsWith('.csv'));
    this.headers.set([]);
    this.mapping.set({});
    this.validation.set(null);
  }

  /**
   * Le mappage de colonnes et le choix d'EPSG n'ont de sens que pour un CSV.
   * Un STR ou un DXF porte sa propre structure : il part directement sur
   * l'import historique, inchangé.
   */
  protected readonly isCsv = signal(true);

  private async importStrOrDxf(): Promise<void> {
    this.busy.set(true);
    try {
      const result = await this.postFile<StrImportResult | null>(
        `Blasts/${this.blastId}/blastFromStr?swapXY=${this.swapXY}`);

      this.fonctionsService.launch_toast(true);
      this.blastChanged = true;

      // Un corps de réponse signifie que le fichier propose un autre nom de tir.
      // On garde la modale ouverte pour le demander : les trous sont déjà importés,
      // refuser ne remet donc rien en cause.
      if (result?.blastId && result?.blastName) {
        this.proposedBlastName.set(result.blastName);
        this.step.set(5);
        return;
      }

      this.close();
    } catch {
      this.fonctionsService.launch_toast(false);
    } finally {
      this.busy.set(false);
    }
  }

  /** Applique au tir le nom porté par le fichier STR. */
  protected async confirmRename(): Promise<void> {
    const name = this.proposedBlastName();
    if (!name) return;

    this.busy.set(true);
    try {
      await firstValueFrom(this.http.put<null, unknown>(
        `Blasts/${this.blastId}/UpdateBlastName?blastName=${encodeURIComponent(name)}`, null));
      this.blastChanged = true;
      this.close();
    } catch {
      this.fonctionsService.launch_toast(false);
    } finally {
      this.busy.set(false);
    }
  }

  /** Conserve le nom actuel : l'import des trous reste acquis. */
  protected declineRename(): void {
    this.close();
  }

  /** Point d'entrée du bouton principal de l'étape 1, selon le format détecté. */
  protected continueFromFile(): Promise<void> {
    return this.isCsv() ? this.goToMapping() : this.importStrOrDxf();
  }

  protected triggerFilePicker(): void {
    document.getElementById('csvHoleFileInput')?.click();
  }

  /** Échantillon de la première ligne, indexé par en-tête */
  protected readonly sample = signal<Record<string, string>>({});

  /** Charge les en-têtes et une ligne d'exemple, puis passe au mappage. */
  protected async goToMapping(): Promise<void> {
    if (!this.file) return;

    this.busy.set(true);
    try {
      const preview = await this.postFile<{ headers: string[], sampleRow: string[] }>(
        `Blasts/${this.blastId}/holesFromCsv/headers`);

      const headers = preview?.headers ?? [];
      const row = preview?.sampleRow ?? [];

      this.headers.set(headers);
      this.sample.set(Object.fromEntries(headers.map((h, i) => [h, row[i] ?? ''])));

      const mapping = this.guessMapping(headers);
      this.mapping.set(mapping);
      this.guessEpsg(mapping, row, headers);

      this.step.set(2);
    } catch {
      this.fonctionsService.launch_toast(false);
    } finally {
      this.busy.set(false);
    }
  }

  /**
   * Devine l'EPSG à partir de l'ordre de grandeur des valeurs.
   * Des coordonnées tenant dans ±180 et ±90 sont des degrés, donc du WGS 84 ;
   * au-delà ce sont des mètres projetés, et seul l'utilisateur connaît le fuseau.
   */
  private guessEpsg(mapping: Record<string, CsvTargetField>, row: string[], headers: string[]): void {
    if (this.sourceEpsg) return;

    const valueOf = (field: CsvTargetField): number | null => {
      const index = headers.findIndex(h => mapping[h] === field);
      if (index < 0) return null;
      const parsed = parseFloat((row[index] ?? '').replace(',', '.'));
      return isNaN(parsed) ? null : parsed;
    };

    const x = valueOf('x');
    const y = valueOf('y');
    if (x === null || y === null) return;

    if (Math.abs(x) <= 180 && Math.abs(y) <= 90) {
      this.sourceEpsg = 4326;
    }
  }

  /**
   * Pré-remplit le mappage à partir des noms de colonnes usuels.
   * L'utilisateur reste libre de tout corriger : ce n'est qu'une amorce.
   */
  private guessMapping(headers: string[]): Record<string, CsvTargetField> {
    const guesses: Record<string, CsvTargetField> = {};

    for (const header of headers) {
      const key = header.trim().toUpperCase();

      if (['HOLEID', 'HOLE', 'NAME', 'NOM', 'ID'].includes(key)) guesses[header] = 'name';
      // Les relevés nomment souvent leurs colonnes End_E / End_N / End_Z
      // La longitude va sur X et la latitude sur Y : c'est l'ordre attendu par la
      // reprojection, et l'inverse de l'ordre officiel de l'EPSG:4326.
      else if (['EASTING', 'X', 'EST', 'END_E', 'E', 'LON', 'LONG', 'LONGITUDE'].includes(key)) guesses[header] = 'x';
      else if (['NORTHING', 'Y', 'NORD', 'END_N', 'N', 'LAT', 'LATITUDE'].includes(key)) guesses[header] = 'y';
      else if (['ELEVATION', 'Z', 'ALTITUDE', 'ELEV', 'END_Z', 'RL', 'ALT'].includes(key)) guesses[header] = 'z';
      else if (['LENGTH', 'LONGUEUR'].includes(key)) guesses[header] = 'length';
      else if (['DIAMETER', 'DIAMETRE', 'DIAMÈTRE', 'DIA'].includes(key)) guesses[header] = 'diameter';
      else if (['AZIMUTH', 'AZIMUT', 'BEARING'].includes(key)) guesses[header] = 'azimuth';
      else if (['ANGLE', 'DIP', 'INCLINAISON'].includes(key)) guesses[header] = 'angle';
      else guesses[header] = 'ignore';
    }

    return guesses;
  }

  protected setMapping(header: string, field: CsvTargetField): void {
    this.mapping.update(m => ({ ...m, [header]: field }));
  }

  /** X, Y et Z sont indispensables ; le nom dépend de la méthode choisie plus loin. */
  protected get mappingIsComplete(): boolean {
    const assigned = Object.values(this.mapping());
    return ['x', 'y', 'z'].every(field => assigned.includes(field as CsvTargetField))
        && !!this.sourceEpsg;
  }

  // ------------------------------------------------------------- étape 3

  protected get methodIsComplete(): boolean {
    return this.dataType !== null;
  }

  protected selectDataType(type: CsvHoleDataType): void {
    this.dataType = type;
  }

  // ------------------------------------------------------------- étape 4

  /** Demande au back de contrôler le fichier, sans rien écrire. */
  protected async goToValidation(): Promise<void> {
    this.busy.set(true);
    try {
      const result = await this.postFile<CsvValidationResult>(
        `Blasts/${this.blastId}/holesFromCsv/validate`, this.buildOptions());
      this.validation.set(result);
      this.step.set(4);
    } catch {
      this.fonctionsService.launch_toast(false);
    } finally {
      this.busy.set(false);
    }
  }

  protected async runImport(): Promise<void> {
    this.busy.set(true);
    try {
      await this.postFile(`Blasts/${this.blastId}/holesFromCsv`, this.buildOptions());
      this.fonctionsService.launch_toast(true);
      this.blastChanged = true;
      this.close();
    } catch {
      this.fonctionsService.launch_toast(false);
    } finally {
      this.busy.set(false);
    }
  }

  // -------------------------------------------------------------- commun

  private buildOptions(): string {
    const byField = (field: CsvTargetField): string | null => {
      const entry = Object.entries(this.mapping()).find(([, value]) => value === field);
      return entry ? entry[0] : null;
    };

    return JSON.stringify({
      mapping: {
        name: byField('name'),
        x: byField('x'),
        y: byField('y'),
        z: byField('z'),
        length: byField('length'),
        diameter: byField('diameter'),
        azimuth: byField('azimuth'),
        angle: byField('angle')
      },
      sourceEpsg: this.sourceEpsg,
      dataType: this.dataType,
      plannedMode: this.plannedMode,
      matchingMethod: this.matchingMethod,
      maxMatchingDistance: this.maxMatchingDistance
    });
  }

  private postFile<T>(uri: string, options?: string): Promise<T> {
    const formData = new FormData();
    formData.append('file', this.file as File);
    if (options !== undefined) {
      formData.append('options', options);
    }

    // postFiles émet un flux d'événements de progression : il faut attendre celui
    // qui porte la réponse, sinon on récupère « requête envoyée » et un corps vide.
    return firstValueFrom(
      (this.http.postFiles(uri, formData) as Observable<HttpEvent<T>>).pipe(
        filter((event): event is HttpResponse<T> => event.type === HttpEventType.Response),
        map(response => response.body as T)
      )
    );
  }

  protected back(): void {
    this.step.update(s => Math.max(1, s - 1));
  }

  protected cancel(): void {
    this.close();
  }

  /** ✕ / Échap du popup : ignorés pendant un appel au back. À l'étape 5, vaut refus du renommage. */
  protected onPopupClosed(): void {
    if (this.busy()) return;
    this.close();
  }

  protected isError(issue: CsvImportIssue): boolean {
    return issue.severity === 1;
  }

  /** `app-input` émet une chaîne : vide ou non numérique devient null. */
  protected toNumber(value: string | number | null): number | null {
    if (value === null || value === '') return null;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  /** `imported` n'est émis qu'une fois, à la fermeture, et seulement si le tir a changé (import ou renommage). */
  private close(): void {
    if (this.blastChanged) this.imported.emit();
    this.blastChanged = false;
    this.closed.emit();
  }

  private buildFieldOptions(): SelectOption[] {
    const fields: [CsvTargetField, string][] = [
      ['ignore', 'FIELD_IGNORE'], ['name', 'FIELD_NAME'], ['x', 'FIELD_X'], ['y', 'FIELD_Y'], ['z', 'FIELD_Z'],
      ['length', 'FIELD_LENGTH'], ['diameter', 'FIELD_DIAMETER'], ['azimuth', 'FIELD_AZIMUTH'], ['angle', 'FIELD_ANGLE']
    ];
    return fields.map(([id, key]) => ({ id, label: this.translate.instant(`CSV_IMPORT.${key}`) }));
  }

  private reset(): void {
    this.fieldOptions = this.buildFieldOptions();
    this.step.set(1);
    this.busy.set(false);
    this.blastChanged = false;
    this.file = null;
    // La popup reste montée : sans ça, re-choisir le même fichier ne déclenche pas `change`
    const fileInput = document.getElementById('csvHoleFileInput') as HTMLInputElement | null;
    if (fileInput) fileInput.value = '';
    this.fileName.set('');
    this.fileSizeKb.set(0);
    this.isCsv.set(true);
    this.headers.set([]);
    this.sample.set({});
    this.mapping.set({});
    this.validation.set(null);
    this.proposedBlastName.set(null);
    this.sourceEpsg = this.siteEpsg;
    this.dataType = null;
    this.plannedMode = CsvPlannedImportMode.Replace;
    this.matchingMethod = CsvMeasuredMatchingMethod.ByName;
    this.maxMatchingDistance = 1.5;
    this.swapXY = true;
  }
}
